import BusinessProviderAuth from '@/components/business/BusinessProviderAuth';
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import useSeo from "@/hooks/useSeo";

import { businessRequest, TOKEN_KEY, DRAFT_KEY } from "@/lib/businessApi";

const CATEGORIES = ["Café", "Restaurant", "Bar / Venue", "Retail", "Gym / Fitness", "Wellness", "Community Venue", "Professional Services", "Other"];

// Country-specific business registration number: label + placeholder per country.
// Countries not listed fall back to a generic "Business Registration Number".
const BUSINESS_NUMBER_LABELS = {
  "Australia": { label: "ABN (Australian Business Number)", placeholder: "51 824 753 556" },
  "New Zealand": { label: "NZBN (New Zealand Business Number)", placeholder: "9429000000000" },
  "United States": { label: "EIN (Employer Identification Number)", placeholder: "12-3456789" },
  "United Kingdom": { label: "Company Registration Number", placeholder: "12345678" },
  "Canada": { label: "Business Number (BN)", placeholder: "123456789" },
  "Singapore": { label: "UEN (Unique Entity Number)", placeholder: "201234567A" },
  "India": { label: "GSTIN / CIN", placeholder: "22AAAAA0000A1Z5" },
};
const COUNTRIES = [...Object.keys(BUSINESS_NUMBER_LABELS), "Other"];
const businessNumberField = (country) => BUSINESS_NUMBER_LABELS[country] || { label: "Business Registration Number", placeholder: "Your local business/company registration number" };

const field = "mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20";

export default function BusinessRegister() {
  useSeo({ title: "Create a Business Account — Orrbbit", description: "Register your Orrbbit Business account and submit it for verification.", path: "/business/register" });
  const navigate = useNavigate();
  const entry=new URLSearchParams(window.location.search).get('entry');
  localStorage.setItem('orrbbit_business_entry',entry==='meetup_spot'?'meetup_spot':'full');
  const [form, setForm] = useState({ owner_name: "", description: "", accepted: false, business_name: "", category: CATEGORIES[0], country: "", address: "", email: "", phone: "", website: "", business_registration_number: "", date_of_birth: "", password: "", confirm: "" });
  const [emailOnly, setEmailOnly] = useState(false);
  const [newAccount, setNewAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (form.password !== form.confirm) { setMsg("Passwords don't match."); return; }
    setLoading(true);
    try {
      const data = await businessRequest('/auth/register', { method: 'POST', token: null, body: {
        name: form.owner_name.trim(), display_name: form.business_name.trim(), account_type: 'business',
        business_entry:localStorage.getItem('orrbbit_business_entry')==='meetup_spot'?'meetup_spot':'full', email: form.email.trim(), password: form.password, date_of_birth: form.date_of_birth, accept_policies: form.accepted,
      }});
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ user_id: data.user.id, profile: {
        name: form.business_name.trim(), category: form.category, email: form.email.trim(),
        country: form.country, location_display: form.address, phone: form.phone, website: form.website,
        registration_number: form.business_registration_number, primary_contact: form.owner_name,
        description: form.description,
      }}));
      await businessRequest('/business/onboarding', { method: 'PUT', body: {
        step: 1, revision: 0, fields: {
          name: form.business_name.trim(), category: form.category, country: form.country,
          email: form.email.trim(), phone: form.phone, website: form.website,
          primaryContact: form.owner_name, description: form.description,
          registrationNumber: form.business_registration_number,
        },
      }});
      navigate('/business/onboarding');
    } catch (error) {
      setMsg(error.message || 'Unable to create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="business-register-page">
      <section className="container-x section-space">
        <Reveal className="mx-auto max-w-lg rounded-3xl border border-cobalt/20 bg-white p-8 shadow-card">
          <div className="mx-auto flex max-w-[220px] flex-col items-end"><img src="/brand/orrbbit-logo.png" alt="Orrbbit" className="h-auto w-full"/><span className="text-xl font-bold text-cobalt">Business</span></div>
          <h1 className="mt-5 text-center font-heading text-2xl font-bold tracking-tight text-navy">Join Orrbbit Business</h1>
          <p className="mt-2 text-center text-sm text-bodygrey">Create your account to promote Events, offers and real-world activity around your Business.</p>
          <BusinessProviderAuth mode="signup" onEmail={() => setEmailOnly(true)} />
          {emailOnly && (!newAccount ? <div className="mt-6 space-y-4">
            <h2 className="text-lg font-semibold">Do you already have an Orrbbit personal account?</h2>
            <Link to="/business/login?setup=1" className="block rounded-full bg-teal px-6 py-3 text-center font-semibold text-white">Yes — log in and add my business</Link>
            <button onClick={() => setNewAccount(true)} className="w-full rounded-full border border-border px-6 py-3 font-semibold">No — create an account</button>
          </div> : <form className="mt-6 space-y-4" onSubmit={submit}>
            <label htmlFor="register-owner" className="block text-sm font-semibold">Your full name<input id="register-owner" data-testid="register-owner" required value={form.owner_name} onChange={set("owner_name")} className={field} autoComplete="name" /></label>
            <label className="block text-sm font-semibold">About your business<textarea data-testid="register-description" required value={form.description} onChange={set("description")} className={field} maxLength={800} /></label>
            <div>
              <label htmlFor="register-name" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Business name *</label>
              <input id="register-name" data-testid="register-name" required minLength={2} value={form.business_name} onChange={set("business_name")} className={field} placeholder="Riverside Cafe" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="register-category" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Category *</label>
                <select id="register-category" data-testid="register-category" value={form.category} onChange={set("category")} className={field}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="register-country" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Country *</label>
                <select id="register-country" data-testid="register-country" required value={form.country} onChange={set("country")} className={field}>
                  <option value="" disabled>Select country…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <p className="text-sm text-bodygrey">After creating your account, search for your business address in the next setup step. Select a suggestion to add its address and map location automatically.</p>
            {form.country && form.country !== "Other" && (
              <div>
                <label htmlFor="register-business-number" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">{businessNumberField(form.country).label}</label>
                <input id="register-business-number" data-testid="register-business-number" value={form.business_registration_number} onChange={set("business_registration_number")} className={field} placeholder={businessNumberField(form.country).placeholder} />
                <p className="mt-1 text-xs text-bodygrey">Required before Orrbbit can move your business to full review.</p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="register-email" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Business email *</label>
                <input id="register-email" data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="you@business.com" />
              </div>
              <div>
                <label htmlFor="register-phone" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Phone</label>
                <input id="register-phone" data-testid="register-phone" value={form.phone} onChange={set("phone")} className={field} placeholder="+61 …" />
              </div>
            </div>
            <div>
              <label htmlFor="register-website" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Website <span className="normal-case text-bodygrey/70">(optional)</span></label>
              <input id="register-website" data-testid="register-website" value={form.website} onChange={set("website")} className={field} placeholder="https://…" />
            </div>
            <div>
              <label htmlFor="register-dob" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Owner's date of birth *</label>
              <input id="register-dob" data-testid="register-dob" type="date" required value={form.date_of_birth} onChange={set("date_of_birth")} className={field} max={new Date(Date.now() - 568080000000).toISOString().slice(0, 10)} />
              <p className="mt-1 text-xs text-bodygrey">You must be 18 or over. This creates your Orrbbit account and is not shown publicly.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="register-password" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Password *</label>
                <input id="register-password" data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className={field} placeholder="Min 8 characters" />
              </div>
              <div>
                <label htmlFor="register-confirm" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Confirm password *</label>
                <input id="register-confirm" data-testid="register-confirm" type="password" required minLength={8} value={form.confirm} onChange={set("confirm")} className={field} placeholder="Repeat password" />
              </div>
            </div>
            <label className="flex items-start gap-3 text-sm"><input type="checkbox" required checked={form.accepted} onChange={e => setForm(f => ({...f, accepted: e.target.checked}))} /> <span>I agree to the <Link to="/terms" className="text-cobalt" target="_blank">Terms</Link> and <Link to="/privacy" className="text-cobalt" target="_blank">Privacy Policy</Link>.</span></label>
            {msg && <p data-testid="register-msg" className="rounded-xl bg-orange/5 px-3 py-2 text-sm text-orange">{msg}</p>}
            <button data-testid="register-submit" type="submit" disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-6 py-3 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>Create Business account <ArrowRight size={16} /></>}
            </button>
          </form>)}
        </Reveal>
      </section>
    </div>
  );
}

